import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getApiBaseUrl, SALON_API_PREFIX } from '../utils/apiConfig';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';
import { persistProductCart } from '../utils/cartPersistence';
import { recordSalonFlowStage } from '../utils/dataSaver';
import { getSkinFlowState, saveSkinFlowState } from '../utils/skinFlowState';
import { getFranchiseId } from '../../shared/franchiseFlowAuth';

function restoreCartFromApiItems(cartAdded) {
  if (!Array.isArray(cartAdded) || cartAdded.length === 0) return null;
  const restored = {};
  cartAdded.forEach((item, idx) => {
    if (!item || typeof item !== 'object') return;
    const product = item.product;
    if (!product || typeof product !== 'object') return;
    const quantity = Number(item.quantity) || 0;
    if (quantity <= 0) return;
    const productId = product.product_id || product.id || product.sku || `cart_${idx}`;
    restored[productId] = { product, quantity };
  });
  return Object.keys(restored).length > 0 ? restored : null;
}

export function useSalonProductCart({
  sessionId,
  appSessionToken,
  pageName = 'SalonResultPage',
  initialCartAdded = null,
}) {
  const apiBaseUrl = useMemo(
    () => (getApiBaseUrl() || '').replace(/\/$/, ''),
    [],
  );

  const [cartItems, setCartItems] = useState(() => {
    const fromReport = restoreCartFromApiItems(initialCartAdded);
    if (fromReport) return fromReport;
    if (!sessionId) return {};
    const stored = getSkinFlowState(sessionId);
    return stored?.cartItems && typeof stored.cartItems === 'object' ? stored.cartItems : {};
  });
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);

  useEffect(() => {
    if (!sessionId) return;
    const current = getSkinFlowState(sessionId) || {};
    saveSkinFlowState(sessionId, { ...current, sessionId, cartItems });
  }, [sessionId, cartItems]);

  useEffect(() => {
    if (!sessionId || !apiBaseUrl || (initialCartAdded && initialCartAdded.length > 0)) return undefined;
    let cancelled = false;

    const params = new URLSearchParams();
    if (appSessionToken) params.set('app_session_token', appSessionToken);
    const franchiseId = getFranchiseId();
    if (franchiseId) params.set('franchise_id', String(franchiseId));
    const qs = params.toString() ? `?${params.toString()}` : '';

    fetchWithTimeout(`${apiBaseUrl}${SALON_API_PREFIX}/get-stored-products/${encodeURIComponent(sessionId)}${qs}`, { timeoutMs: 20000 })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        const restored = restoreCartFromApiItems(data?.cart_added);
        if (!restored) return;
        setCartItems((prev) => (
          prev && Object.keys(prev).length > 0 ? prev : restored
        ));
      })
      .catch(() => {});

    return () => { cancelled = true; };
  }, [sessionId, appSessionToken, apiBaseUrl, initialCartAdded]);

  const handleAddToCart = useCallback((product, quantity, productId) => {
    const prevQty = cartItems[productId]?.quantity || 0;
    if (prevQty === 0 && quantity > 0 && sessionId) {
      recordSalonFlowStage(sessionId, 'add_to_cart', appSessionToken || null, {
        page_name: pageName,
        quantity,
      }).catch(() => {});
    }
    setCartItems((prev) => {
      if (quantity === 0) {
        const next = { ...prev };
        delete next[productId];
        return next;
      }
      return { ...prev, [productId]: { product, quantity } };
    });
  }, [appSessionToken, cartItems, pageName, sessionId]);

  const getTotalCartItems = useCallback(
    () => Object.values(cartItems).reduce((sum, item) => sum + (item.quantity || 0), 0),
    [cartItems],
  );

  const handleCheckout = useCallback(() => {
    if (isCheckoutModalOpen) return;
    const cartProducts = Object.entries(cartItems).filter(([, item]) => item.quantity > 0);
    if (cartProducts.length === 0) {
      alert('Your cart is empty. Please add products to cart first.');
      return;
    }
    if (sessionId) {
      recordSalonFlowStage(sessionId, 'checkout', appSessionToken || null, {
        page_name: pageName,
      }).catch(() => {});
    }
    setIsCheckoutModalOpen(true);
  }, [appSessionToken, cartItems, isCheckoutModalOpen, pageName, sessionId]);

  const handleCloseCheckoutModal = useCallback(() => setIsCheckoutModalOpen(false), []);
  const handleCartUpdate = useCallback((updatedCartItems) => setCartItems(updatedCartItems), []);

  const handleViewProduct = useCallback((product, productUrl) => {
    const numericProductId = Number(product?.id ?? product?.product_id);
    const productId = Number.isFinite(numericProductId) ? numericProductId : null;

    if (sessionId) {
      recordSalonFlowStage(sessionId, 'product_viewed', appSessionToken || null, {
        page_name: pageName,
      }).catch(() => {});
    }

    if (apiBaseUrl && sessionId && productId) {
      fetchWithTimeout(`${apiBaseUrl}${SALON_API_PREFIX}/record-viewed-product`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          skin_id: sessionId,
          session_id: sessionId,
          product_id: productId,
          ...(appSessionToken && { app_session_token: appSessionToken }),
        }),
        timeoutMs: 10000,
      }).catch(() => {});
    }

    if (productUrl) {
      window.open(productUrl, '_blank', 'noopener,noreferrer');
    }
  }, [apiBaseUrl, appSessionToken, pageName, sessionId]);

  const saveCartToBackend = useCallback((items) => {
    if (!apiBaseUrl || !sessionId) return;
    persistProductCart(apiBaseUrl, sessionId, appSessionToken, items);
  }, [apiBaseUrl, appSessionToken, sessionId]);

  const skipNextCartPersistRef = useRef(true);
  useEffect(() => {
    if (skipNextCartPersistRef.current) {
      skipNextCartPersistRef.current = false;
      return;
    }
    saveCartToBackend(cartItems);
  }, [cartItems, saveCartToBackend]);

  const checkoutCartItems = useMemo(
    () => Object.entries(cartItems)
      .filter(([, item]) => item.quantity > 0)
      .map(([productId, item]) => ({ ...item, productId })),
    [cartItems],
  );

  return {
    cartItems,
    checkoutCartItems,
    isCheckoutModalOpen,
    handleAddToCart,
    handleCheckout,
    handleCloseCheckoutModal,
    handleCartUpdate,
    handleViewProduct,
    getTotalCartItems,
    apiBaseUrl,
    pageName,
  };
}

export default useSalonProductCart;
